import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, ChevronDown, Search } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { listGuardToursReview } from "@/lib/review.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/review/tours")({
  component: ToursReviewPage,
});

function ToursReviewPage() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const fetchTours = useServerFn(listGuardToursReview);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["review-tours", orgId, from, to],
    queryFn: () =>
      fetchTours({
        data: { orgId: orgId!, from: from || undefined, to: to || undefined, limit: 100 },
      }),
    enabled: !!orgId,
  });

  const [expanded, setExpanded] = useState<string | null>(null);

  if (!orgId) return <EmptyState text="Select an organization" />;
  if (isLoading)
    return (
      <div className="flex items-center gap-2 py-12 text-[#C0C0C0]">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading tours…
      </div>
    );
  if (error) return <LoadErrorState onRetry={() => void refetch()} retrying={isFetching} />;
  const runs = data?.runs ?? [];
  const filteredRuns = runs.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.tourName.toLowerCase().includes(q) ||
      r.siteName.toLowerCase().includes(q) ||
      r.guardName.toLowerCase().includes(q)
    );
  });
  if (filteredRuns.length === 0) return <EmptyState text="No guard tours in the selected window." />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <Field label="From">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white"
          />
        </Field>
        <Field label="To">
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white"
          />
        </Field>
        <Field label="Search">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#999]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tours..."
              className="rounded-md border border-white/10 bg-black/40 pl-8 pr-3 py-1.5 text-sm text-white placeholder:text-[#666]"
            />
          </div>
        </Field>
      </div>

      {filteredRuns.map((r) => {
        const open = expanded === r.id;
        const completed = r.status === "completed";
        return (
          <div
            key={r.id}
            className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setExpanded(open ? null : r.id)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.04]"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-white">
                    {r.tourName}
                  </span>
                  <StatusBadge status={r.status} />
                </div>
                <div className="mt-0.5 text-xs text-[#999999]">
                  {r.guardName} · {r.siteName} ·{" "}
                  {new Date(r.startedAt).toLocaleString()}
                  {r.endedAt && ` → ${new Date(r.endedAt).toLocaleTimeString()}`}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3 text-xs text-[#C0C0C0]">
                <span>
                  {r.checkpoints.length} checkpoint{r.checkpoints.length === 1 ? "" : "s"}
                </span>
                {r.missedCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-400">
                    <AlertTriangle className="h-3.5 w-3.5" /> {r.missedCount} missed
                  </span>
                )}
                {completed && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
                <ChevronDown
                  className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
                />
              </div>
            </button>
            {open && (
              <div className="border-t border-white/10 bg-black/30 px-4 py-3">
                {r.checkpoints.length === 0 ? (
                  <div className="text-xs text-[#999999]">No scans recorded.</div>
                ) : (
                  <div className="space-y-2">
                    {r.checkpoints.map((cp) => (
                      <div
                        key={cp.checkpointId}
                        className="flex items-center justify-between rounded-md bg-white/[0.03] px-3 py-2 text-xs"
                      >
                        <div>
                          <span className="font-semibold text-white">
                            #{cp.sequence} {cp.name}
                          </span>
                          {cp.code && (
                            <span className="ml-2 text-[#999999]">({cp.code})</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-[#C0C0C0]">
                          <span>
                            {cp.count} scan{cp.count === 1 ? "" : "s"}
                          </span>
                          <span>last {new Date(cp.lastScan).toLocaleTimeString()}</span>
                        </div>
                      </div>
                    ))}
                    <details className="mt-2 text-[11px] text-[#999999]">
                      <summary className="cursor-pointer hover:text-white">
                        Full scan log ({r.scans.length})
                      </summary>
                      <ul className="mt-2 space-y-1">
                        {r.scans.map((s) => (
                          <li key={s.id}>
                            #{s.sequence} {s.checkpointName} —{" "}
                            {new Date(s.scannedAt).toLocaleString()}
                          </li>
                        ))}
                      </ul>
                    </details>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    completed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    in_progress: "bg-[#CC0000]/15 text-[#FF8A8A] border-[#CC0000]/40",
    abandoned: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  };
  const cls = map[status] ?? "bg-white/10 text-[#C0C0C0] border-white/20";
  return (
    <span className={cn("rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide", cls)}>
      {status.replace("_", " ")}
    </span>
  );
}

/**
 * Friendly fallback for data-loading failures. Technical detail stays in the
 * server logs; guards and supervisors only ever see plain language plus a retry.
 */
function LoadErrorState({ onRetry, retrying }: { onRetry: () => void; retrying?: boolean }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center">
      <div className="text-sm font-semibold text-white">We couldn't load guard tours</div>
      <p className="mx-auto mt-1 max-w-sm text-xs text-[#999999]">
        Something went wrong on our end while loading this list. Your tour records are safe. Try
        again, and contact support if it keeps happening.
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="mt-4 inline-flex min-h-[44px] items-center rounded-lg border border-white/15 px-4 text-xs font-semibold uppercase tracking-wider text-white hover:bg-white/[0.06] disabled:opacity-60"
      >
        {retrying ? "Retrying…" : "Try again"}
      </button>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-sm text-[#999999]">
      {text}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-[#C0C0C0]">
      <span>{label}</span>
      {children}
    </label>
  );
}
